/**
 * Welle 1 (Cockpit-Intelligenz) W1-C — FindingDrilldown: lazy evidence detail
 * for ONE expanded worklist row (Aktion 1a).
 *
 * Reference: architecture-cockpit-intelligence.md §Interfaces (Backend —
 * Drill-down) + modules-cockpit-intelligence.md W1-C. Fetches
 * `getCockpitDocumentDetail` EXACTLY ONCE per mount (row-expand), never for a
 * collapsed row.
 *
 * Reviewer Re-Loop M1 (2026-07-08, „wieder öffnen unerreichbar"): W1-B filtert
 * bereits aufgelöste Findings aus der AKTIVEN Worklist (`item.findings` in
 * `AdvisorCockpitPage` ist deshalb IMMER `resolution === null`) — die
 * Reopen-/Badge-Zweige von `FindingResolveControls` waren dadurch nie
 * erreichbar. Der Drill-down-Callable liefert dagegen ALLE Findings des
 * Belegs inkl. ihres aktuellen `resolution`-Stands, weil er direkt aus
 * `document_checks` + `check_resolutions` liest, nicht aus der bereits
 * netto-gefilterten Worklist. Diese Komponente ist daher NICHT mehr rein
 * darstellend: der neue Abschnitt "Geklärte & ignorierte Auffälligkeiten"
 * rendert für jedes Finding mit `resolution !== null` ein
 * {@link ./FindingResolveControls} (Badge + „wieder öffnen"), gespeist aus
 * genau denselben, hier ohnehin schon geladenen Detaildaten — kein
 * zusätzlicher Read. Aktive Findings bleiben unverändert in der
 * `item.findings`-Schleife von `AdvisorCockpitPage` (resolve/ignorieren).
 *
 * SICHERHEIT: purely cosmetic (StB-only AdvisorCockpit, itself beta-gated).
 * All hard checks (Loop-B-Beta + Advisor-Scope) live server-side in beiden
 * Callables (`getCockpitDocumentDetail` read, `resolveCheckFinding` write via
 * `FindingResolveControls`); a foreign mandant yields `permission-denied`
 * either way.
 *
 * DE ist maßgeblich (StB-only Beta-Panel, Muster `KontierungVorschlagPanel`) —
 * inline German labels, keine i18n-6-Locale-Pflicht für dieses Panel; nur die
 * Reason-Labels (`cockpit.reason.*`) und Basistexte (`cockpit.drilldown.*` /
 * `cockpit.resolve.*`) nutzen i18n, weil sie mit dem bestehenden
 * Cockpit-Wortschatz geteilt werden.
 *
 * @module components/moat/FindingDrilldown
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconWarning } from '../../icons';
import {
  getCockpitDocumentDetail,
  type CockpitDocumentDetailResult,
  type CockpitDocumentDetailFinding,
} from '../../api/cockpitDocumentDetailApi';
import { resolveReasonLabel } from '../../utils/cockpitReason';
import FindingResolveControls from './FindingResolveControls';

type Status =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; detail: CockpitDocumentDetailResult };

/** de-DE date+time formatting for a millis timestamp; `null` ⇒ fallback dash. */
function fmtDateTime(millis: number | null): string {
  if (millis === null) return '–';
  try {
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(millis),
    );
  } catch {
    return '–';
  }
}

/** de-DE currency formatting; null/invalid ⇒ dash. */
function fmtAmount(value: number | null, currency: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '–';
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency && currency.length === 3 ? currency : 'EUR',
    }).format(value);
  } catch {
    return `${value.toFixed(2)} €`;
  }
}

/** Renders one evidence key/value pair; objects/arrays are JSON-stringified. */
const EvidenceRow: React.FC<{ label: string; value: unknown }> = ({ label, value }) => {
  const display =
    value === null || value === undefined
      ? '–'
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-faint">{label}</span>
      <span className="text-secondary tabular-nums">{display}</span>
    </div>
  );
};

/**
 * Structured evidence panel for ONE flagged inbound document. Loads the full
 * drill-down detail on mount and renders: which invoice/supplier/date, every
 * finding's structured evidence, the supplier IBAN history (previous vs.
 * current, last seen), the resolution audit trail, AND (Reviewer Re-Loop M1)
 * a reachable "wieder öffnen" control for every already-resolved/-ignored
 * finding of this document — the active worklist never carries a non-null
 * `resolution`, so this is the only place that data (and the reopen action)
 * is reachable.
 *
 * @param mandantUid the currently selected mandant scope.
 * @param docId the inbound document being drilled into.
 * @param onResolved called after a successful resolve/ignore/reopen write on
 *   one of the RESOLVED findings rendered here, so the parent can refetch the
 *   worklist (same contract as {@link ./FindingResolveControls}'s own prop).
 */
const FindingDrilldown: React.FC<{
  mandantUid: string;
  docId: string;
  onResolved: () => void;
}> = ({ mandantUid, docId, onResolved }) => {
  const { t } = useTranslation('common');
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  /**
   * Localised reason label with `{{amount}}` interpolated from the finding's
   * `evidence.taxTotal` (Jurist-Auflage / e-rechnung A-2 wording). Delegates
   * to the shared {@link resolveReasonLabel} (fix cockpit-worklist-amount) —
   * this is the ONE place `evidence` is actually available, so it is passed
   * through unchanged; `AdvisorCockpitPage`'s two call sites call the same
   * helper without `evidence` (never available on the worklist shape) and
   * always get the clean amount-less fallback.
   */
  const renderReason = (f: CockpitDocumentDetailFinding): string =>
    resolveReasonLabel(t, f.code, f.evidence);

  useEffect(() => {
    let cancelled = false;
    setStatus({ kind: 'loading' });
    getCockpitDocumentDetail(mandantUid, docId)
      .then((detail) => {
        if (!cancelled) setStatus({ kind: 'ready', detail });
      })
      .catch(() => {
        if (!cancelled) setStatus({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [mandantUid, docId]);

  if (status.kind === 'loading') {
    return (
      <div className="rounded-md bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-muted">
        {t('cockpit.drilldown.loading', 'Details werden geladen …')}
      </div>
    );
  }
  if (status.kind === 'error') {
    return (
      <div className="rounded-md bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-[var(--tone-danger-fg)]">
        <IconWarning size={12} className="mr-1 inline" />
        {t('cockpit.drilldown.loadError', 'Details konnten nicht geladen werden.')}
      </div>
    );
  }

  const { detail } = status;
  const { ibanHistory, resolutionTrail, bankInfo } = detail;
  // Reviewer Re-Loop M1: the only findings NOT present (as active) in
  // `item.findings` on the worklist are exactly the resolved/ignored ones —
  // this is where their reopen action becomes reachable.
  const resolvedFindings = detail.findings.filter((f) => f.resolution !== null);

  return (
    <div className="space-y-3 rounded-md border border-hairline bg-[rgb(var(--surface-base-rgb)/0.6)] p-3">
      {/* Document identity */}
      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <EvidenceRow label={t('cockpit.drilldown.invoiceLabel', 'Rechnung')} value={detail.invoiceNumber || '–'} />
        <EvidenceRow label={t('cockpit.drilldown.supplierLabel', 'Lieferant')} value={detail.supplierName || '–'} />
        <EvidenceRow label={t('cockpit.drilldown.dateLabel', 'Datum')} value={detail.documentDate || '–'} />
        <EvidenceRow
          label={t('cockpit.drilldown.amountLabel', 'Betrag')}
          value={fmtAmount(detail.grossTotal, detail.currency)}
        />
      </div>

      {/* B4 (architecture-goal-durchstich-fixplan.md §1 B4 / §2 F-Doku): die
          Steuer-Kontierung (`inbound_taxDecision`) ist Owner-only v1 (Jurist J-3,
          `functions/src/inbound/setInboundTaxDecision.ts`) — der StB sieht die
          Findings, kann die Kontierung selbst aber NICHT ändern. Dieses Panel
          ist StB-only (Modul-Kopf „SICHERHEIT: purely cosmetic (StB-only
          AdvisorCockpit)"), daher unbedingt (kein Owner/Advisor-Conditional
          nötig) — der Hinweis macht die v1-Grenze an genau der Stelle sichtbar,
          an der der StB ein Finding ansieht. */}
      {detail.findings.length > 0 && (
        <p className="text-xs italic text-faint">
          {t('cockpit.drilldown.advisorCorrectionHint', 'Die Korrektur der Kontierung erfolgt durch den Mandanten.')}
        </p>
      )}

      {/* Per-finding evidence */}
      {detail.findings.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">
            {t('cockpit.drilldown.evidenceTitle', 'Belegte Fakten')}
          </h4>
          <ul className="space-y-2">
            {detail.findings.map((f) => (
              <li key={`${f.checkId}-${f.fingerprint}`} className="rounded bg-[rgb(var(--surface-raised-rgb)/0.6)] p-2">
                <div className="mb-1 text-xs font-medium text-secondary">
                  {renderReason(f)}
                </div>
                {Object.keys(f.evidence).length === 0 ? (
                  <p className="text-xs text-faint">{t('cockpit.drilldown.noEvidence', 'Keine strukturierten Fakten.')}</p>
                ) : (
                  <div className="space-y-0.5">
                    {Object.entries(f.evidence).map(([key, value]) => (
                      <EvidenceRow key={key} label={key} value={value} />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Reviewer Re-Loop M1 — resolved/ignored findings, with a reachable
          "wieder öffnen" per finding. Only rendered when at least one finding
          of this document has a non-null resolution. */}
      {resolvedFindings.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">
            {t('cockpit.drilldown.resolvedFindingsTitle', 'Geklärte & ignorierte Auffälligkeiten')}
          </h4>
          <ul className="space-y-2">
            {resolvedFindings.map((f) => (
              <li key={`${f.checkId}-${f.fingerprint}-resolved`} className="rounded bg-[rgb(var(--surface-raised-rgb)/0.4)] p-2">
                <div className="mb-1.5 text-xs font-medium text-secondary">
                  {renderReason(f)}
                </div>
                <FindingResolveControls
                  mandantUid={mandantUid}
                  docId={docId}
                  checkId={f.checkId}
                  resolution={f.resolution}
                  onResolved={onResolved}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Welle 2 (W2-1c) — bank/BIC display, only rendered when the server
          resolved a DE IBAN against the committed Bundesbank BLZ asset. No
          empty box for non-DE IBANs / unresolved BLZ (bankInfo === null). */}
      {bankInfo !== null && (
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">
            {t('cockpit.drilldown.bankName', 'Bank')} / {t('cockpit.drilldown.bic', 'BIC')}
          </h4>
          <div className="space-y-0.5">
            <EvidenceRow label={t('cockpit.drilldown.bankName', 'Bank')} value={bankInfo.bankName} />
            <EvidenceRow label={t('cockpit.drilldown.bic', 'BIC')} value={bankInfo.bic} />
            <EvidenceRow label={t('cockpit.drilldown.blz', 'Bankleitzahl')} value={bankInfo.blz} />
          </div>
        </div>
      )}

      {/* IBAN history */}
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">
          {t('cockpit.drilldown.ibanHistoryTitle', 'IBAN-Historie')}
        </h4>
        {ibanHistory ? (
          <div className="space-y-0.5">
            <EvidenceRow
              label={t('cockpit.drilldown.previousIban', 'Bisherige IBAN')}
              value={ibanHistory.previousIban ?? '–'}
            />
            <EvidenceRow
              label={t('cockpit.drilldown.currentIban', 'Aktuelle IBAN')}
              value={ibanHistory.lastIban ?? '–'}
            />
            <EvidenceRow
              label={t('cockpit.drilldown.lastSeenAt', 'Zuletzt gesehen am')}
              value={fmtDateTime(ibanHistory.lastSeenAt)}
            />
          </div>
        ) : (
          <p className="text-xs text-faint">
            {t('cockpit.drilldown.noIbanHistory', 'Keine IBAN-Historie vorhanden.')}
          </p>
        )}
      </div>

      {/* Resolution audit trail */}
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">
          {t('cockpit.drilldown.auditTrailTitle', 'Verlauf')}
        </h4>
        {resolutionTrail.length === 0 ? (
          <p className="text-xs text-faint">
            {t('cockpit.drilldown.noAuditTrail', 'Noch keine Auflösungs-Historie.')}
          </p>
        ) : (
          <ul className="space-y-1">
            {resolutionTrail.map((r, idx) => (
              <li key={`${r.checkId}-${r.createdAt}-${idx}`} className="text-xs text-muted">
                <span className="font-medium text-secondary">{r.checkId}</span> —{' '}
                {t(`cockpit.resolve.${r.resolutionType === 'reopened' ? 'reopen' : r.resolutionType === 'resolved' ? 'markResolved' : 'markIgnored'}`, r.resolutionType)}{' '}
                ({r.resolvedByRole}, {fmtDateTime(r.createdAt)})
                {r.note && <span className="italic text-faint"> — „{r.note}“</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Jurist-Empfehlung: stehender, dezenter Nicht-Beratungs-Hinweis — einmal
          pro Finding-Detailansicht sichtbar. Grenzt den automatischen
          Plausibilitätshinweis klar von einer steuerlichen Beratung ab. */}
      <p className="border-t border-hairline pt-2 text-xs italic text-faint">
        {t('cockpit.drilldown.autoHintDisclaimer', 'Automatischer Plausibilitätshinweis, keine steuerliche Beratung.')}
      </p>
    </div>
  );
};

export default FindingDrilldown;
